import React, { useState, useEffect } from 'react';
import { Sidebar } from './Dashboard';
import BenchmarkTabs from './BenchmarkTabs';
import BenchmarkPicker from '../BenchmarkPicker';
import { API } from '../App';

const SPR_VALUES = [1.0, 1.5, 2.0, 2.5, 3.0, 3.5, 4.0, 4.5, 5.0, 5.5, 6.0, 6.5, 7.0, 7.5, 8.0, 8.5, 9.0];

const styles = {
  tagManual: {
    display: 'inline-block',
    padding: '1px 7px',
    marginLeft: '8px',
    borderRadius: '5px',
    backgroundColor: '#5b3a8c',
    color: '#ffffff',
    fontSize: '10px',
    fontWeight: 'bold',
    letterSpacing: '0.5px',
    verticalAlign: 'middle',
  },
  tagLive: {
    display: 'inline-block',
    padding: '1px 7px',
    marginLeft: '8px',
    borderRadius: '5px',
    backgroundColor: '#0f3460',
    color: '#a0a0b0',
    fontSize: '10px',
    fontWeight: 'bold',
    letterSpacing: '0.5px',
    verticalAlign: 'middle',
  },
  container: {
    minHeight: '100vh',
    backgroundColor: '#0f0f1a',
    display: 'flex',
  },
  gameTab: {
    padding: '10px 20px',
    backgroundColor: '#0f3460',
    border: '2px solid #0f3460',
    borderRadius: '8px',
    color: '#a0a0b0',
    fontSize: '14px',
    fontWeight: 'bold',
    cursor: 'pointer',
    marginRight: '8px',
    marginBottom: '24px',
  },
  gameTabActive: {
    padding: '10px 20px',
    backgroundColor: '#0f3460',
    border: '2px solid #e94560',
    borderRadius: '8px',
    color: '#ffffff',
    fontSize: '14px',
    fontWeight: 'bold',
    cursor: 'pointer',
    marginRight: '8px',
    marginBottom: '24px',
  },
  main: {
    flex: 1,
    padding: '40px',
    overflowY: 'auto',
  },
  pageTitle: {
    color: '#ffffff',
    fontSize: '28px',
    fontWeight: 'bold',
    marginBottom: '8px',
  },
  pageSubtitle: {
    color: '#a0a0b0',
    fontSize: '14px',
    marginBottom: '40px',
  },
  card: {
    backgroundColor: '#16213e',
    borderRadius: '16px',
    padding: '24px',
    marginBottom: '24px',
  },
  cardTitle: {
    color: '#ffffff',
    fontSize: '18px',
    fontWeight: 'bold',
    marginBottom: '8px',
  },
  cardSubtitle: {
    color: '#a0a0b0',
    fontSize: '13px',
    marginBottom: '24px',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
  },
  th: {
    color: '#a0a0b0',
    fontSize: '12px',
    letterSpacing: '1px',
    textTransform: 'uppercase',
    padding: '12px 16px',
    textAlign: 'left',
    borderBottom: '1px solid #0f3460',
  },
  td: {
    color: '#ffffff',
    fontSize: '14px',
    padding: '14px 16px',
    borderBottom: '1px solid #0f3460',
    verticalAlign: 'middle',
  },
  sprLabel: {
    color: '#ffffff',
    fontSize: '16px',
    fontWeight: 'bold',
  },
  autoValue: {
    color: '#a0a0b0',
    fontSize: '13px',
    fontStyle: 'italic',
  },
  autoCount: {
    color: '#a0a0b0',
    fontSize: '11px',
    marginTop: '2px',
  },
  input: {
    width: '90px',
    padding: '8px 12px',
    backgroundColor: '#0f3460',
    border: '1px solid #0f3460',
    borderRadius: '8px',
    color: '#ffffff',
    fontSize: '14px',
    boxSizing: 'border-box',
    outline: 'none',
  },
  toggleRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  toggleLabel: {
    color: '#a0a0b0',
    fontSize: '12px',
  },
  toggleOff: {
    width: '36px',
    height: '20px',
    backgroundColor: '#0f3460',
    borderRadius: '10px',
    cursor: 'pointer',
    border: 'none',
    outline: 'none',
    position: 'relative',
  },
  toggleOn: {
    width: '36px',
    height: '20px',
    backgroundColor: '#e94560',
    borderRadius: '10px',
    cursor: 'pointer',
    border: 'none',
    outline: 'none',
    position: 'relative',
  },
  saveButton: {
    padding: '12px 32px',
    backgroundColor: '#e94560',
    border: 'none',
    borderRadius: '8px',
    color: '#ffffff',
    fontSize: '14px',
    fontWeight: 'bold',
    cursor: 'pointer',
    marginTop: '16px',
  },
  success: {
    color: '#00cc00',
    fontSize: '14px',
    marginBottom: '16px',
  },
  error: {
    color: '#e94560',
    fontSize: '14px',
    marginBottom: '16px',
  },
  hphValue: {
    color: '#e94560',
    fontSize: '15px',
    fontWeight: 'bold',
  },
  noData: {
    color: '#a0a0b0',
    fontSize: '12px',
    fontStyle: 'italic',
  },
};

function getSprColor(spr) {
  const ratio = (spr - 1) / 6;
  const r = Math.round(30 + ratio * 225);
  const g = Math.round(100 - ratio * 80);
  const b = Math.round(220 - ratio * 190);
  return `rgb(${r},${g},${b})`;
}

function HphBenchmarks() {
  const isAdmin = localStorage.getItem('admin_role') === 'admin';
  const token = localStorage.getItem('admin_token');
  const [benchmarks, setBenchmarks] = useState([]);
  const [overrides, setOverrides] = useState({});
  const [manualFlags, setManualFlags] = useState({});
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [gameTypes, setGameTypes] = useState([]);
  const [selectedGame, setSelectedGame] = useState(null);
  const [editable, setEditable] = useState(true);
  const [sourceName, setSourceName] = useState(null);
  const [frozenLabel, setFrozenLabel] = useState(null);
  const [isCustom, setIsCustom] = useState(false);

  useEffect(() => {
    fetch(`${API}/game-types`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json())
      .then(data => {
        if (Array.isArray(data)) {
          setGameTypes(data);
          if (data.length > 0) setSelectedGame(data[0]);
        }
      });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (selectedGame) loadBenchmarks();
  }, [selectedGame]); // eslint-disable-line react-hooks/exhaustive-deps

  async function loadBenchmarks() {
    if (!selectedGame) return;
    const res = await fetch(`${API}/admin/hph-benchmarks?game_type_id=${selectedGame.id}`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    const data = await res.json();
    const rows = Array.isArray(data) ? data : (data.rows || []);
    setEditable(data.editable !== false);
    setSourceName(data.source_name || null);
    setFrozenLabel(data.frozen_label || null);
    setIsCustom(!!data.is_custom);
    setBenchmarks(rows);
    const ov = {};
    const mf = {};
    rows.forEach(b => {
      ov[b.spr] = {
        hph: b.target_hph || '',
        rph: b.target_rph || '',
      };
      mf[b.spr] = b.is_manual || false;
    });
    setOverrides(ov);
    setManualFlags(mf);
  }

  async function saveBenchmarks() {
    setLoading(true);
    setError('');
    try {
      const updates = SPR_VALUES.map(spr => ({
        spr,
        target_hph: overrides[spr]?.hph ? parseFloat(overrides[spr].hph) : null,
        target_rph: overrides[spr]?.rph ? parseFloat(overrides[spr].rph) : null,
        is_manual: manualFlags[spr] || false,
      }));
      const res = await fetch(`${API}/admin/hph-benchmarks`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ game_type_id: selectedGame.id, benchmarks: updates }),
      });
      if (res.ok) {
        setMessage('HPH Benchmarks saved successfully');
        setTimeout(() => setMessage(''), 3000);
        loadBenchmarks();
      } else {
        setError('Failed to save');
      }
    } catch (e) {
      setError('Could not connect to server');
    }
    setLoading(false);
  }

  function updateOverride(spr, field, value) {
    setOverrides(prev => ({
      ...prev,
      [spr]: { ...prev[spr], [field]: value }
    }));
  }

  function toggleManual(spr) {
    setManualFlags(prev => ({ ...prev, [spr]: !prev[spr] }));
  }

  const benchmarkMap = {};
  benchmarks.forEach(b => { benchmarkMap[b.spr] = b; });

  // What reports compare a test against: a manual override if one is set, otherwise
  // the value frozen by the Benchmark Period (or the running average until one closes).
  function benchValue(value, isManual, isFrozen, color) {
    const num = parseFloat(value);
    if (!num) return <span style={styles.noData}>{isManual ? '—' : 'No data'}</span>;
    return (
      <span style={{ color, fontSize: '16px', fontWeight: 'bold' }}>
        {num}
        {isManual && <span style={styles.tagManual}>MANUAL</span>}
        {!isManual && !isFrozen && <span style={styles.tagLive}>RUNNING AVERAGE</span>}
      </span>
    );
  }

  // Every test recorded so far, for reference — not what anyone is scored against
  // once a Benchmark Period has closed.
  function runningHph(b) {
    if (!b.running_hph && !b.running_rph) return <span style={styles.noData}>No data yet</span>;
    return (
      <div>
        <span style={styles.autoValue}>
          {b.running_hph ? `${b.running_hph} HPH` : '—'}
          {b.running_rph ? ` / ${b.running_rph} RPH` : ''}
        </span>
        <div style={styles.autoCount}>{b.auto_count || 0} test{b.auto_count === 1 ? '' : 's'}</div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <Sidebar active="Benchmarks" />
      <div style={styles.main}>
        <BenchmarkTabs current="hph" />
        <div style={styles.pageTitle}>HPH & RPH Benchmarks</div>
        <div style={styles.pageSubtitle}>
          Set target Hands Per Hour and Rounds Per Hour for each Spots Per Round value — per game
        </div>

        {message && <div style={styles.success}>{message}</div>}
        {error && <div style={styles.error}>{error}</div>}

        <div>
          {gameTypes.map(game => (
            <button
              key={game.id}
              style={selectedGame?.id === game.id ? styles.gameTabActive : styles.gameTab}
              onClick={() => setSelectedGame(game)}
            >
              {game.name}
            </button>
          ))}
        </div>

        {selectedGame && !editable && (
          <div style={{ ...styles.cardSubtitle, backgroundColor: '#16213e', border: '2px solid #b8860b', borderRadius: '8px', padding: '14px', color: '#FFD700', marginBottom: '16px' }}>
            {isCustom ? (
              <>🔒 This is a custom game. It is measured against <b>{sourceName}</b>'s RPH/HPH benchmark,
              and its own tests never change it.</>
            ) : (
              <>🔒 RPH/HPH is shared across the full and EZ versions (they run at the same speed).
              These values are managed on the <b>{sourceName}</b> tab — the auto averages here include this game's sessions too.</>
            )}
          </div>
        )}

        {selectedGame && editable && (
          <BenchmarkPicker
            gameTypeId={selectedGame.id}
            canEdit={isAdmin}
            onChange={() => loadBenchmarks()}
          />
        )}

        {selectedGame && (
        <div style={styles.card}>
          <div style={styles.cardTitle}>{selectedGame.name} — Benchmarks by SPR</div>
          <div style={styles.cardSubtitle}>
            {frozenLabel
              ? `Benchmark columns: the stored targets from ${frozenLabel}. `
              : 'Benchmark columns: the running average, until a Benchmark Period is closed. '}
            A manual override replaces them. The running average on the right pools every test across the
            full &amp; EZ versions, for reference only. Color indicates SPR load — blue = light, red = heavy.
          </div>
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>SPR</th>
                <th style={styles.th}>Benchmark HPH{frozenLabel ? ` — ${frozenLabel}` : ''}</th>
                <th style={styles.th}>Benchmark RPH</th>
                <th style={styles.th}>Manual override</th>
                <th style={styles.th}>Running average</th>
              </tr>
            </thead>
            <tbody>
              {SPR_VALUES.map(spr => {
                const b = benchmarkMap[spr] || {};
                const isManual = manualFlags[spr] || false;
                const color = getSprColor(spr);
                return (
                  <tr key={spr}>
                    <td style={styles.td}>
                      <span style={{
                        ...styles.sprLabel,
                        color,
                        backgroundColor: color + '22',
                        padding: '4px 12px',
                        borderRadius: '20px',
                        border: `1px solid ${color}`,
                      }}>
                        {spr}
                      </span>
                    </td>
                    <td style={styles.td}>
                      {benchValue(isManual ? overrides[spr]?.hph : b.auto_hph, isManual, b.frozen, '#00d4ff')}
                    </td>
                    <td style={styles.td}>
                      {benchValue(isManual ? overrides[spr]?.rph : b.auto_rph, isManual, b.frozen, '#FFD700')}
                    </td>
                    <td style={styles.td}>
                      <div style={styles.toggleRow}>
                        <button
                          style={isManual ? styles.toggleOn : styles.toggleOff}
                          onClick={() => toggleManual(spr)}
                          disabled={!isAdmin || !editable}
                        />
                        {isManual && (
                          <>
                            <input
                              style={{ ...styles.input, width: '72px' }}
                              type="number"
                              step="0.5"
                              value={overrides[spr]?.hph || ''}
                              onChange={e => updateOverride(spr, 'hph', e.target.value)}
                              placeholder="HPH"
                              disabled={!editable}
                            />
                            <input
                              style={{ ...styles.input, width: '72px' }}
                              type="number"
                              step="0.5"
                              value={overrides[spr]?.rph || ''}
                              onChange={e => updateOverride(spr, 'rph', e.target.value)}
                              placeholder="RPH"
                              disabled={!editable}
                            />
                          </>
                        )}
                      </div>
                    </td>
                    <td style={styles.td}>
                      {runningHph(b)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {isAdmin && editable && (
            <button style={styles.saveButton} onClick={saveBenchmarks} disabled={loading}>
              {loading ? 'Saving...' : 'Save Benchmarks'}
            </button>
          )}
        </div>
        )}
      </div>
    </div>
  );
}

export default HphBenchmarks;